import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Renderer is called recursiveley to generate the page.  The config and data for the component tree
 * needs to be preloaded and passed to the renderer.
 */
import React from 'react';
import { clone, get } from 'lodash-es';
import { ErrorBoundary } from 'react-error-boundary';
import Registry from './Registry';
import LayoutWrapper from './LayoutWrapper';
/** Takes the props and converts to propsObject, bringing in any dynamic data */
const mapProps = (props, data) => {
    if (!props) {
        return undefined;
    }
    const newProps = clone(props);
    Object.entries(props).forEach(([key, value]) => {
        if (typeof value === 'string' && value.startsWith('@')) {
            // value is the path to the object in data
            newProps[key] = get(data, value.substring(1));
        }
        // value is the literal value
    });
    return newProps;
};
export const getConfigFromContext = (context, id) => (context.portalConfigs[id] || context.defaultConfigs[id]);
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    console.log('Error on the render', error);
    // airbrake.notify({
    //   err: error,
    // });
    return (_jsxs("div", { role: "alert", children: [_jsx("p", { children: "Something went wrong:" }), _jsx("pre", { children: error.message }), _jsx("button", { type: "button", onClick: resetErrorBoundary, children: "Try again" })] }));
};
/**
 * Preloads all data and dependencies required to fully render the page
 * @param same props used to render component
 * @returns Promise that resolves when the data is loaded.
 */
export const getData = async ({ configId, data, context, editData, // only used when editing
 }, store) => {
    const config = getConfigFromContext(context, configId);
    // const label = `getData: ${config?.component}:${configId}`;
    // console.log(label);
    if (!config) {
        console.log('No config found in context', {
            // context,
            configId,
        });
        return configId;
    }
    const { pageDefinition } = context;
    if (!pageDefinition) {
        console.log('No page definition');
        return configId;
    }
    // first check to see if i am in the override list
    // check from child to parent so
    if (pageDefinition.overrides[configId]) {
        const resolvedConfigId = pageDefinition.overrides[configId][0];
        const results = await getData({
            configId: resolvedConfigId,
            data,
            context,
            editData,
        }, store);
        return results;
    }
    const componentConfig = config;
    const componentName = componentConfig.component;
    const component = Registry[componentName];
    if (component) {
        const props = {
            ...mapProps(componentConfig.props, data),
            context,
            data,
            editData,
            configId,
            config,
        };
        if (component.getData) {
            const componentDataResults = component.getData(props, store);
            return componentDataResults;
        }
        // console.log('No getData on component', componentName);
        return configId;
    }
    console.log(`render failed for config ${configId}`, config);
    return configId;
};
const Renderer = ({ configId, data, context, editData, // only used when editing
 }) => {
    // console.log('Rendering', {
    //   configId,
    //   data,
    //   context,
    //   editData,
    // });
    const config = getConfigFromContext(context, configId);
    if (!config) {
        return editData && editData.editing ? (_jsx("div", { children: "No Config" })) : null;
    }
    const { pageDefinition } = context;
    if (!pageDefinition) {
        return (_jsx("div", { children: "No pageDefinition in scope" }));
    }
    // first check to see if i am in the override list
    // check from child to parent so
    if (pageDefinition.overrides[configId]) {
        const resolvedConfigId = pageDefinition.overrides[configId][0];
        return (_jsx(Renderer, { configId: resolvedConfigId, data: data, context: context, editData: editData }));
    }
    const componentConfig = config;
    const componentName = componentConfig.component;
    const component = Registry[componentName];
    if (component) {
        const canEdit = editData
            && editData.editing
            && context.pageDefinition?.ownerMap[configId]
                === context.pageDefinition?.templateList[0];
        // console.log(`Setting canEdit on ${configId}`, canEdit);
        const props = {
            ...mapProps(componentConfig.props, data),
            context,
            data,
            editData,
            configId,
            config,
            disabled: !canEdit,
        };
        // console.log(`Rendering ${componentName}. props:`, props, data);
        const element = React.createElement(component, props);
        const elementWithErrorBoundery = (_jsx(ErrorBoundary, { FallbackComponent: ErrorFallback, children: _jsx(LayoutWrapper, { layout: componentConfig.props && componentConfig.props.layout, children: element }) }));
        if (editData && editData.editing) {
            return (_jsx(editData.EditWrapper, { id: configId, context: context, editData: editData, disabled: !canEdit, children: elementWithErrorBoundery }));
        }
        return elementWithErrorBoundery;
    }
    console.log(`render failed for config ${configId}`, config);
    return (_jsxs("div", { children: ["Not Recognized:", componentName] }));
};
Renderer.defaultProps = {
    editData: undefined,
};
export default Renderer;
//# sourceMappingURL=Renderer.js.map